import { Component, OnInit, SecurityContext } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { DomSanitizer } from "@angular/platform-browser";
import { ActivatedRoute, Router } from "@angular/router";
import { firstValueFrom, of } from "rxjs";
import { switchMap, takeUntil } from "rxjs/operators";
import { AppConstants } from "src/app/constants/constants";
import { Modes } from "src/app/constants/modes";
import { ContentReplacedDialog } from "src/app/views/learning/content-replaced-dialog";
import { SkillsCheckContent } from "src/app/_models/SkillsCheck";
import { Course } from "src/app/_models/Course";
import { ScormEntry } from "src/app/_models/ScormEntry";
import { ScormSession } from "src/app/_models/ScormSession";
import { CourseService } from "src/app/_services/course.service";
import { LearningTrackingService } from "src/app/_services/learning-tracking.service";
import { PublicContentObjectService } from "src/app/_services/public-content-object.service";
import { PublicUserSessionService } from "src/app/_services/public-user-session.service";
import { ScormEntryService } from "src/app/_services/scorm-entry.service";
import { ScormReceiverService } from "src/app/_services/scorm-receiver.service";
import { BaseComponent } from "../base/base-component.component";

@Component({
  selector: "app-external-runtime",
  templateUrl: "./external-runtime.component.html",
  styleUrls: ["./external-runtime.component.scss"],
})
export class ExternalRuntimeComponent extends BaseComponent implements OnInit {
  isScormContentReplaced: boolean = false;
  fromLearningRecordTab: boolean = false;
  fromLearningPlan: boolean = false;
  objectId: string = undefined;
  objectName: string = undefined;
  courseId: number;
  path: string = undefined;
  course: Course;
  lessonMode: Modes = Modes.Lesson;
  skillsCheckObjectId: string;
  topicId: number;
  learnerId: number;
  fromLearningResources: boolean = false;
  fromViewLearningPlan: boolean = false;

  constructor(
    public dialog: MatDialog,
    private contentService: PublicContentObjectService,
    private courseService: CourseService,
    private userSessionService: PublicUserSessionService,
    private learningTrackingService: LearningTrackingService,
    private scormEntryService: ScormEntryService,
    private scormReceiverService: ScormReceiverService,
    private route: ActivatedRoute,
    public sanitizer: DomSanitizer,
    private router: Router
  ) {
    super();
  }

  ngOnInit(): void {
    this.objectId = this.route.snapshot.paramMap.get(AppConstants.OBJECT_ID);
    this.objectName = this.route.snapshot.queryParamMap.get(
      AppConstants.OBJECT_NAME
    );
    this.courseId = +this.route.snapshot.queryParamMap.get(
      AppConstants.COURSE_ID
    );
    this.skillsCheckObjectId = this.route.snapshot.queryParamMap.get(
      AppConstants.SKILL_CHECK_OBJECT_ID
    );
    this.topicId = +this.route.snapshot.queryParamMap.get(
      AppConstants.TOPIC_ID
    );
    this.fromLearningRecordTab = JSON.parse(
      this.route.snapshot.queryParamMap.get(
        AppConstants.FROM_LEARNING_RECORD_TAB
      )
    );
    this.fromLearningPlan = JSON.parse(
      this.route.snapshot.queryParamMap.get(AppConstants.FROM_LEARNING_PLAN)
    );
    this.learnerId = JSON.parse(
      this.route.snapshot.queryParamMap.get(AppConstants.LEARNER_ID)
    );

    this.fromLearningResources = JSON.parse(
      this.route.snapshot.queryParamMap.get(
        AppConstants.FROM_LEARNING_RESOURCES
      )
    );

    this.fromViewLearningPlan = JSON.parse(
      this.route.snapshot.queryParamMap.get(
        AppConstants.FROM_VIEW_LEARNING_PLAN
      )
    );

    this.loadExternalLink();
  }

  loadExternalLink(): void {
    this.courseService.getCourseById$(this.courseId).subscribe((course) => {
      this.course = course;

      if (!course) alert(AppConstants.FAILED_SERVICE_RESPONSE);
    });
    this.contentService
      .getPathByObjectId$(this.objectId)
      .subscribe((contentPath) => {
        if (contentPath.includes(AppConstants.CONTENT_REPLACED))
          this.isScormContentReplaced = true;
        else this.isScormContentReplaced = false;

        this.path = contentPath;
      });
  }

  goToLink(): void {
    var externalWindow = window.open("", "_blank");

    if (this.fromLearningResources || this.fromViewLearningPlan) {
      this.redirectToExternalLink(externalWindow);
      return;
    }

    if (this.learnerId) {
      if (this.path.includes(AppConstants.CONTENT_REPLACED)) {
        this.openContentReplacedDialog();
      } else {
        this.redirectToExternalLink(externalWindow);
      }
    } else {
      this.learningTrackingService
        .updateLearningObjectDateVisitedByObjectId$(this.objectId)
        .pipe(
          switchMap(() => {
            if (this.fromLearningPlan && !this.isScormContentReplaced) {
              return this.scormReceiverService.createSessionByObjectId$(
                this.objectId
              );
            } else return of({});
          }),
          switchMap((session: ScormSession) => {
            if (session.id) {
              let scormEntry = <ScormEntry>{
                scormSessionId: session.id,
                elementName: AppConstants.CORE_LESSON_STATUS,
                elementValue: AppConstants.VISITED,
                dateCreated: new Date(),
                dateModified: new Date(),
              };

              return this.scormEntryService.addScormEntry$(scormEntry);
            } else return of({});
          }),
          switchMap(() => {
            if (this.topicId == 0) return of(null);

            var skillsCheckContent: SkillsCheckContent = {
              learnerLevelId: this.topicId,
              courseId: this.courseId,
            };

            return this.learningTrackingService.assignContentForSkillsCheck$(
              skillsCheckContent
            );
          })
        )
        .subscribe(() => {
          if (this.path.includes(AppConstants.CONTENT_REPLACED)) {
            this.openContentReplacedDialog();
          } else {
            this.redirectToExternalLink(externalWindow);
          }
        });
    }
  }

  redirectToExternalLink(pdfWindow: Window): void {
    pdfWindow.location.replace(this.getSanitizedUrl());
  }

  async redirectToContentPlayer(): Promise<void> {
    await firstValueFrom(this.userSessionService
      .setIsAllowAccessContent$(AppConstants.TRUE));

    if (this.path.includes(AppConstants.CONTENT_REPLACED)) {
      if (this.fromLearningResources || this.fromViewLearningPlan) {
        this.openContentReplacedDialog();
        return;
      }
      this.learningTrackingService
        .updateLearningObjectDateVisitedByObjectId$(this.objectId)
        .pipe(
          takeUntil(this.destroy$),
          switchMap(() => {
            if (this.fromLearningPlan) {
              return this.scormReceiverService.createSessionByObjectId$(
                this.objectId
              );
            } else return of({});
          }),
          switchMap((session: ScormSession) => {
            if (session.id) {
              let scormEntry = <ScormEntry>{
                scormSessionId: session.id,
                elementName: AppConstants.CORE_LESSON_STATUS,
                elementValue: AppConstants.VISITED,
                dateCreated: new Date(),
                dateModified: new Date(),
              };

              return this.scormEntryService.addScormEntry$(scormEntry);
            } else return of({});
          }),
          switchMap(() => {
            if (this.topicId != 0) {
              var skillsCheckContent: SkillsCheckContent = {
                learnerLevelId: this.topicId,
                courseId: this.courseId,
              };

              return this.learningTrackingService.assignContentForSkillsCheck$(
                skillsCheckContent
              );
            } else return of({});
          })
        )
        .subscribe(() => {
          this.openContentReplacedDialog();
        });
    } else {
      this.router.navigate(
        [AppConstants.CONTENT_PLAYER_PAGE + `/${this.objectId}`],
        {
          queryParams: {
            mode:
              this.fromLearningRecordTab || this.fromLearningPlan
                ? Modes.LessonTracked
                : Modes.Lesson,
            fromLearningRecordTab: this.fromLearningRecordTab,
          },
        }
      );
    }
  }

  openContentReplacedDialog(): void {
    this.dialog.open(ContentReplacedDialog, {
      id: AppConstants.CONTENT_REPLACED_DIALOG_ID,
    });
  }

  private getSanitizedUrl(): string {
    return this.sanitizer.sanitize(
      SecurityContext.URL,
      this.sanitizer.bypassSecurityTrustResourceUrl(this.path)
    );
  }
}
